import type {Element} from 'hast'
import {source} from 'unist-util-source'
import {SKIP, visit} from 'unist-util-visit'

import type {CopilotMarkdownExtension, ReactComponentsExtension, RehypeHtmlTransformer} from '../extension'
import {dataAttrToPropName} from '../utils'
import {isTrustedMediaSource} from './trusted-media-domains'

/**
 * Resolves an untrusted image `src` supplied by the caller. Return an inline `data:image/...;base64,` URL to render
 * it, `null` to render an "image not available" placeholder, or `undefined` to keep the default behavior.
 */
export type ImageSourceResolver = (src: string) => string | null | undefined

const RESOLVED_SRC_ATTRIBUTE = 'data-copilot-resolved-src'
const RESOLVED_SRC_PROPERTY = dataAttrToPropName(RESOLVED_SRC_ATTRIBUTE)
const INLINE_IMAGE_DATA_URL = /^data:image\/(?:png|jpeg|gif|webp);base64,[A-Za-z0-9+/]+={0,2}$/

function textSpan(value: string, className?: string[]): Element {
  return {
    type: 'element',
    tagName: 'span',
    properties: className ? {className} : {},
    children: [{type: 'text', value}],
  }
}

/**
 * Requires that images in rendered markdown come from an explicitly allow-listed domain, unless the caller's
 * resolver maps the source to an inline image.
 */
const ensureTrustedSource =
  (resolveImageSrc?: ImageSourceResolver): RehypeHtmlTransformer =>
  (tree, file) =>
    visit(tree, (node, index, parent) => {
      if (node.type !== 'element') return
      if (node.tagName === 'img' && 'src' in node.properties) {
        const src = node.properties.src as string
        if (isTrustedMediaSource(src)) return

        const resolved = resolveImageSrc?.(src)
        if (typeof resolved === 'string' && INLINE_IMAGE_DATA_URL.test(resolved)) {
          node.properties[RESOLVED_SRC_PROPERTY] = resolved
          return
        }

        const alt = typeof node.properties.alt === 'string' ? node.properties.alt : ''
        const replacement =
          resolved === null
            ? textSpan(alt ? `Image not available: ${alt}` : 'Image not available', ['fgColor-muted', 'f6'])
            : textSpan(source(file, node) ?? '')
        if (parent && typeof index === 'number') {
          parent.children[index] = replacement
          return [SKIP, index]
        }
      }
    })

const reactComponents: ReactComponentsExtension = {
  img: (props, fallthrough) => {
    const resolvedSrc = props[RESOLVED_SRC_ATTRIBUTE]
    if (typeof resolvedSrc !== 'string') return fallthrough
    const {node: _node, [RESOLVED_SRC_ATTRIBUTE]: _resolved, ...imgProps} = props
    return <img {...imgProps} src={resolvedSrc} alt={imgProps.alt ?? ''} />
  },
}

export default function imagesExtension({
  resolveImageSrc,
}: {resolveImageSrc?: ImageSourceResolver} = {}): CopilotMarkdownExtension {
  return {
    transformHtml: ensureTrustedSource(resolveImageSrc),
    reactComponents,
  }
}
